// Local interfaces to avoid circular dependencies
export interface HistogramDataItem {
  time_of_day: string;
  consumption: number;
  gCO_2: number;
  cost?: number;
  period_len: number;
}

export interface PercentileData {
  first_25_percent: HistogramDataItem[];
  first_50_percent: HistogramDataItem[];
  first_75_percent: HistogramDataItem[];
  remaining_25_percent: HistogramDataItem[];
}

// Helper function to format time period
export const formatTimePeriod = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes}m`;
  }
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (remainingMinutes === 0) {
    return `${hours}h`;
  }
  
  return `${hours}h ${remainingMinutes}m`;
};

export interface ProcessedPercentileData {
  percentile: string;
  duration: string;
  total_consumption: number;
  total_emissions: number;
  total_cost?: number;
  emissions_per_kwh: number;
  consumption_type?: string;
  color?: string;
}

// Helper function to create category labels based on column width
const createCategoryLabel = (baseLabel: string, fullLabel: string, isMobile?: boolean): string => {
  if (isMobile) {
    return baseLabel;
  }
  return fullLabel;
};

// Helper function to process percentile data
export const processPercentileData = (
  percentileData: PercentileData | null, 
  consumptionType: string,
  theme?: any,
  isMobile?: boolean,
  displayMode: 'consumption' | 'cost' = 'consumption'
): ProcessedPercentileData[] => {
  if (!percentileData) return [];
  
  const summaryData: ProcessedPercentileData[] = [];

  const addBand = (
    items: HistogramDataItem[],
    percentile: string,
    fullLabel: string,
    color: string
  ) => {
    if (items.length === 0) return;
    const totalConsumption = items.reduce((sum, item) => sum + item.consumption, 0);
    const totalEmissions = items.reduce((sum, item) => sum + item.gCO_2, 0);
    const totalCost = items.reduce((sum, item) => sum + (item.cost ?? 0), 0);
    const emissionsPerKwh = totalConsumption > 0 ? totalEmissions / totalConsumption : 0;
    const durationMinutes = items.reduce((sum, item) => sum + item.period_len, 0);
    summaryData.push({
      percentile: createCategoryLabel(percentile, fullLabel, isMobile),
      duration: formatTimePeriod(durationMinutes),
      total_consumption: totalConsumption,
      total_emissions: totalEmissions,
      total_cost: totalCost,
      emissions_per_kwh: emissionsPerKwh,
      consumption_type: consumptionType,
      color
    });
  };
  
  addBand(
    percentileData.first_25_percent,
    'Highest',
    displayMode === 'cost' ? 'Highest (Top 25% of total cost)' : 'Highest (Top 25% of total)',
    theme?.palette?.mode === 'dark' ? '#d32f2f' : '#d32f2f'
  );
  addBand(
    percentileData.first_50_percent,
    'High',
    displayMode === 'cost' ? 'High (Top 50% of total cost)' : 'High (Top 50% of total)',
    theme?.palette?.mode === 'dark' ? '#f57c00' : '#f57c00'
  );
  addBand(
    percentileData.first_75_percent,
    'Moderate',
    displayMode === 'cost' ? 'Moderate (Bottom 50% of total cost)' : 'Moderate (Bottom 50% of total)',
    theme?.palette?.mode === 'dark' ? '#fbc02d' : '#fbc02d'
  );
  addBand(
    percentileData.remaining_25_percent,
    'Lowest',
    displayMode === 'cost' ? 'Lowest (Bottom 25% of total cost)' : 'Lowest (Bottom 25% of total)',
    theme?.palette?.mode === 'dark' ? '#388e3c' : '#388e3c'
  );
  
  return summaryData;
};

// Helper function to add totals row to processed data
export const addTotalsRow = (summaryData: ProcessedPercentileData[]): ProcessedPercentileData[] => {
  if (summaryData.length === 0) return summaryData;
  
  const totalConsumption = summaryData.reduce((sum, row) => sum + row.total_consumption, 0);
  const totalEmissions = summaryData.reduce((sum, row) => sum + row.total_emissions, 0);
  const totalCost = summaryData.reduce((sum, row) => sum + (row.total_cost ?? 0), 0);
  
  const weightedEmissionsPerKwh = totalConsumption > 0 ? totalEmissions / totalConsumption : 0;
  
  summaryData.push({
    percentile: 'Total',
    duration: '',
    total_consumption: totalConsumption,
    total_emissions: totalEmissions,
    total_cost: totalCost,
    emissions_per_kwh: weightedEmissionsPerKwh,
    consumption_type: summaryData[0]?.consumption_type
  });
  
  return summaryData;
};
